import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { addDays, subDays, format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function DailyRecapsViewer() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableDates, setAvailableDates] = useState(new Set());
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [recapHtml, setRecapHtml] = useState('');
  const [isLoadingRecap, setIsLoadingRecap] = useState(false);
  const [error, setError] = useState('');
  const iframeRef = useRef(null);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
      iframe.style.height = iframe.contentDocument.body.scrollHeight + 'px';
    }
  };

  const fetchAvailableDates = async () => {
    setIsLoadingDates(true);
    try {
      const response = await fetch(`${API_BASE}/api/recaps/available-dates`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load recap dates');
      }

      const data = await response.json();
      setAvailableDates(new Set(data.dates || []));
    } catch (err) {
      console.error('Error loading recap dates:', err);
      setError('Could not load recap dates.');
      setAvailableDates(new Set());
    } finally {
      setIsLoadingDates(false);
    }
  };

  const fetchRecapForDate = async (dateValue) => {
    const formattedDate = format(dateValue, 'yyyy-MM-dd');
    const fileName = `daily_recap_${formattedDate}.html`;
    
    setIsLoadingRecap(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/recaps/view/${encodeURIComponent(fileName)}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No recap available for this date');
        }
        throw new Error('Failed to load recap');
      }

      const html = await response.text();
      const fontStyle = '<style>body,table,td,th,p,div,span { font-size: 95% !important; }</style>';
      const styledHtml = html.includes('</head>')
        ? html.replace('</head>', `${fontStyle}</head>`)
        : fontStyle + html;
      setRecapHtml(styledHtml);
    } catch (err) {
      console.error('Error loading recap:', err);
      setError(err.message || 'Could not load recap for this date.');
      setRecapHtml('');
    } finally {
      setIsLoadingRecap(false);
    }
  };

  useEffect(() => {
    fetchAvailableDates();
  }, []);

  useEffect(() => {
    fetchRecapForDate(selectedDate);
  }, [selectedDate]);

  const getDayClassName = (date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    return availableDates.has(formattedDate) ? undefined : 'react-datepicker__day--unavailable';
  };

  return (
    <div className="content-card">
      <h2>Daily Transfer Recaps</h2>
      <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-lg)' }}>
        View the daily summary of all data transfers to S3 buckets (measurements, intercomparison data, quicklooks).
      </p>

      <div className="plot-controls" style={{ marginBottom: 'var(--space-lg)' }}>
        <label className="control-label">Date</label>
        <div className="date-nav">
          <button
            className="date-nav-btn"
            onClick={() => setSelectedDate(prev => subDays(prev, 1))}
            title="Previous day"
          >
            ←
          </button>

          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date || new Date())}
            dateFormat="yyyy-MM-dd"
            dayClassName={getDayClassName}
            disabled={isLoadingDates}
          />

          <button
            className="date-nav-btn"
            onClick={() => setSelectedDate(prev => addDays(prev, 1))}
            title="Next day"
          >
            →
          </button>

          {isLoadingDates && (
            <span className="loading-indicator">
              <span className="loading-spinner"></span>
              Loading dates...
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ 
          color: 'var(--color-error)', 
          backgroundColor: 'var(--color-error-light)',
          padding: 'var(--space-md)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-md)'
        }}>
          {error}
        </div>
      )}

      {!error && isLoadingRecap && (
        <div className="loading-state">
          <span className="loading-spinner"></span>
          Loading recap...
        </div>
      )}

      {!error && !isLoadingRecap && recapHtml && (
        <div className="plot-container">
          <iframe
            ref={iframeRef}
            title="Daily Transfer Recap"
            srcDoc={recapHtml}
            onLoad={handleIframeLoad}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'white',
              display: 'block',
              minHeight: '400px'
            }}
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      )}
    </div>
  );
}

export default DailyRecapsViewer;
